import { useEffect, useState } from 'react';
import { getTenantByDomain, type TenantInfo } from '../api/publicApi';
import { getImageUrl } from '../utils/s3ImageUtils';

/**
 * Detect tenant domain from hostname.
 * admin.cityplace.app → cityplace.app
 * cityplace.localhost → cityplace.app (local dev)
 */
function detectTenantDomain(): string | null {
  const hostname = window.location.hostname;

  if (hostname.endsWith('.localhost') || hostname.endsWith('.local')) {
    const sub = hostname.split('.')[0];
    const localMap: Record<string, string> = {
      cityplace: 'cityplace.app',
      capcana: 'capcana.app',
      zabelabeach: 'zabelabeach.app',
      canabay: 'canabay.app',
    };
    return localMap[sub] || null;
  }

  if (hostname === 'localhost' || hostname === '127.0.0.1') return null;

  const parts = hostname.split('.');
  if (parts.length >= 2) return parts.slice(-2).join('.');
  return null;
}

/**
 * Hook that detects tenant by domain, caches branding,
 * sets CSS variable, page title, and favicon.
 */
export function useTenantBranding() {
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const domain = detectTenantDomain();
    if (!domain) {
      setLoading(false);
      return;
    }

    getTenantByDomain(domain)
      .then(async (t) => {
        if (t) {
          setTenant(t);

          // Set CSS variable
          const color = t.settings?.primaryColor || '#000000';
          document.documentElement.style.setProperty('--tenant-primary', color);

          // Set page title
          if (t.name) {
            document.title = t.name;
          }

          // Set favicon if configured
          const favicon = t.settings?.favicon;
          if (favicon) {
            try {
              const faviconUrl = await getImageUrl(favicon);
              const link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
                || document.createElement('link');
              link.rel = 'icon';
              link.href = faviconUrl;
              if (!link.parentNode) document.head.appendChild(link);
            } catch {}
          }
        }
      })
      .finally(() => setLoading(false));
  }, []);

  return {
    tenant,
    tenantLoading: loading,
    // The id is what /login needs in X-Tenant-Id. Before login there is no
    // token, so useTenant — which reads the tenant out of the token — has
    // nothing yet. This domain lookup, already done here for the logo and the
    // colours, is the only thing that knows the community at that point.
    tenantId: tenant?.id || '',
    tenantLogo: tenant?.settings?.logo || '',
    tenantName: tenant?.name || '',
    tenantPrimaryColor: tenant?.settings?.primaryColor || '#000000',
    // Public web key (tenant.settings.public_api_key). Only valid for the guest
    // flow endpoints; the backend never serves the first-party api_key here.
    tenantApiKey: tenant?.settings?.apiKey || '',
    // SAM-1065: WhatsApp codes are offered only when tenant-branding says so; missing means off.
    whatsappCode: tenant?.features?.whatsapp_code === true,
  };
}
